import React from 'react';
import Content from '../components/content';
import Image from '../components/image';
import Footer from '../components/footer';
import Layout from '../components/layout';
import CaseHeader from '../components/case-header';

const Typeform = () => (
    <Layout>
        <CaseHeader
            title="Docplanner"
            role="Lead Systems Designer"
            time="2021&ndash;2022"
            imgSrc={require('../assets/images/docplanner/header-1.gif')}
            alt="Docplanner's design system"
            description="Docplanner is a platform that allows doctors to find patients and book appointments."
            quote="Docplanner’s mission is to make healthcare faster and easier for everyone."
            quoteAuthor="Tomasz Wieczorek"
            quoteAuthorRole="CEO of Docplanner"
        />
        <section>
            <Content kind="p" tag="h2">
                Intro
            </Content>
            <Content>
                Before 2022, Docplanner relied on a UI library called UI Kit, built on top of
                Bootstrap and used across several products. While it was initially fast and
                cost-effective, it became harder to maintain as the company and its product
                ecosystem grew, creating challenges around consistency and scalability.
            </Content>
            <Content>
                In 2022, I joined Docplanner to lead the creation of a new Design Systems team. Our
                goal was to maintain the existing library while building a new design system for the
                company’s SaaS product, <a href="https://watson.docplanner.design/">Watson</a>,
                laying the foundations for a more scalable and consistent interface.{' '}
            </Content>
        </section>
        <section>
            <Content kind="p" tag="h2">
                Initial issues
            </Content>
            <Content>
                Supporting two products: The UI Kit library served two very different products: a
                SaaS platform for doctors focused on efficiency and daily workflows, and a
                marketplace centered on discoverability and information display. Because of these
                differences, many components couldn’t be reused across both products.
            </Content>
            <Content>
                Limited flexibility: UI Kit relied heavily on third-party libraries with limited
                customization. This made the system harder to evolve with our product needs and
                resulted in outdated components.
            </Content>
            <Content>
                Accessibility gaps : Accessibility had not been considered when the foundations or
                components were created. Combined with third-party dependencies, this created
                significant WCAG debt, including missing ARIA roles, poor keyboard navigation, and
                insufficient color contrast.
            </Content>
            <Content>
                No shared language : Documentation was fragmented between designers and developers,
                with different naming conventions and component descriptions. This caused confusion
                and slowed teams down.
            </Content>
        </section>
        <section>
            <Content kind="p" tag="h2">
                Watson
            </Content>
            <Content>
                After evaluating the effort required to fix the existing system, we concluded that
                starting from scratch was the better approach. Since the previous library had been
                built mainly for the marketplace, we proposed creating a dedicated team to build a
                new design system tailored for our SaaS product.{' '}
            </Content>
            <Content>
                We aimed to build a system that addressed these issues at the root: a flexible
                solution tailored to our needs, a shared language between design and development,
                accessibility built in from the start, and clear, comprehensive documentation.
            </Content>
            <Image
                alt="JSON file sample (left) with dynamically generated content. The prototype (right) filled with the new content ready to be used in user test sessions."
                src={require('../assets/images/typeform/fake-data-json.jpg')}
            />
        </section>
        <section>
            <Content kind="p" tag="h2">
                Foundations
            </Content>
            <Content>
                We began by defining the foundational design elements—spacing, typography, and
                colors—to establish a scalable and semantically consistent system. I led the
                typography work, creating a new type scale, defining styles, and implementing the
                related tokens and component specifications.
            </Content>
        </section>
        <Footer>
            <a href="./new-relic">
                <img
                    loading="lazy"
                    src={require('../assets/images/new-relic/header-3.gif')}
                    alt={'New Relic use case'}
                />
            </a>
            <a href="./typeform">
                <img
                    loading="lazy"
                    src={require('../assets/images/typeform/header-3.gif')}
                    alt={'Typeform use case'}
                />
            </a>
        </Footer>
    </Layout>
);

export default Typeform;
